import { ReactNode, useEffect, useState } from 'react';
import { EFieldType, EValueType, IField, IFieldOption, IForm, IPage } from '../../../models/form/form';
import { Alert, AlertTitle, Box, Button, Grid2 as Grid, Paper, Typography } from '@mui/material';
import Progress from '../../../components/progress/Progress';
import { useTranslation } from 'react-i18next';
import FormService from '../../../services/form/FormService';
import InputField from '../../../components/form/fields/InputField';
import SelectField from '../../../components/form/fields/SelectField';
import ImageUploaderField from '../../../components/form/fields/ImageUploaderField';
import IBuilding from '../../../models/building';
import IFloor from '../../../models/floor';
import ILocal from '../../../models/local';
import RatingField from '../../../components/form/fields/RatingField';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import imageCompression from 'browser-image-compression';
import { IPropertyGroup } from '../../../models/propertyGroup';

type FormToNode = (field: IField, form: IForm) => ReactNode;

interface ServiceRequestFormProps {
  form: IForm;
  onChange: (f: IForm) => void;
  propertyGroup?: IPropertyGroup;
  building?: IBuilding;
  floor?: IFloor;
  local: ILocal;
}

const ServiceRequestForm = ({ form, onChange, propertyGroup, building, floor, local }: ServiceRequestFormProps) => {
  const { t } = useTranslation(['common', 'form']);
  const [isRequiredCompleted, setIsRequiredCompleted] = useState(false);
  const ignoreTexts = new Set([EFieldType.Text]);
  const [images, setImages] = useState<Map<string, File[]>>(new Map());
  const [isSuccessDisplayed, setIsSuccessDisplayed] = useState(false);
  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<IPage>();

  useEffect(() => {
    if (!form) return;

    setCurrentPage(form.pages.find((page) => page.pageNumber === currentPageNumber));
  }, [currentPageNumber, form]);

  const updateField = (field: IField, options: IFieldOption[]) => {
    const updatedPages = form.pages.map((page) => {
      if (currentPageNumber !== page.pageNumber) return page;

      const updatedFields = page.fields.map((currentField) => currentField === field ? { ...field, values: options } : currentField);

      return { ...page, fields: updatedFields };
    });

    onChange({ ...form, pages: updatedPages });
  };

  const fieldTypes: Map<EFieldType, FormToNode> = new Map<EFieldType, FormToNode>([
    [
      EFieldType.Text,
      (field: IField) => (
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {field.text}
        </Typography>
      )
    ],
    [EFieldType.Input, (field: IField) => <InputField field={field} onChange={(fieldOption) => updateField(field, fieldOption)} />],
    [EFieldType.Select, (field: IField) => <SelectField field={field} onChange={(options) => updateField(field, options)} />],
    [EFieldType.Image, (field: IField) => (
      <ImageUploaderField
        images={images.get(field.id) || []}
        setImages={(newImages: File[]) => {
          setImages((prev) => new Map(prev).set(field.id, newImages));
        }}
      />
    )],
    [EFieldType.Rating, (field: IField) => <RatingField field={field} onChange={(options) => updateField(field, options)} />]
  ]);

  useEffect(() => {
    if (!currentPage) return;

    const isRequiredEmpty = currentPage.fields.some((field) => field.required && (!field.values || !field.values[0] || !field.values[0].value));
    const isAllBoolChecked = currentPage.fields.filter((field) => field.valueType === EValueType.Boolean).every((field) => field.values && field.values.length && field.values[0].value === 'true');

    setIsRequiredCompleted(!isRequiredEmpty && isAllBoolChecked);
  }, [form.pages.map(page => page.fields)]);

  if (!form) return <div></div>;

  const onSubmit = async () => {
    try {
      if (!form) return;

      setIsLoading(true);

      const formToSend = { ...form, localId: local.id }
      await onProcessImageFields();
      await FormService.PostFilledForm(formToSend)
      FormService.SendFormEmail(formToSend)

      setIsSuccessDisplayed(true);
    } catch (error) {
      setIsErrorDisplayed(true);
    }
    finally {
      setIsLoading(false);
    }
  };

  const onProcessImageFields = async () => {
    const uploadPromises = form.pages.flatMap((page) => {
      return page.fields.map(async (field) => {
        const fieldImages = images.get(field.id);
        if (!fieldImages || !fieldImages.length) return;

        const compressedImages = await Promise.all(fieldImages.map((image) => compressImage(image)))

        const imageKeys = await onSendImages(compressedImages);
        if (!imageKeys) return;

        imageKeys.forEach((image, index) => {
          if (!field.values) field.values = [];
          field.values.push({ value: image, id: index });
        });
      });
    });

    await Promise.all(uploadPromises);
  };

  const compressImage = async (image: File) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920
    }

    const compressedImage: Blob = await imageCompression(image, options)
    return new File([compressedImage], image.name);
  }

  const onSendImages = async (images: File[]) => {
    const formData = new FormData();
    images.forEach((image) => formData.append('file', image));

    return await FormService.UploadImages(formData)
  }

  return (
    <Paper sx={{ borderRadius: 4, px: 3, paddingTop: 3, margin: 'auto', marginTop: 4, overflowY: "scroll", minWidth: { xs: "90%", md: "60%" }, maxWidth: "100%" }}>
      <Box marginBottom={4} sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
        <Typography variant="h5" color="primary" fontWeight="medium" marginBottom={2}>
          {form && form.name}
        </Typography>
        <Grid container>
          {building && (
            <Grid size={12}>
              <Typography variant="subtitle2" color="primary" align="center">{t("form:building")}: {building.name}</Typography>
            </Grid>
          )}
          {floor && (
            <Grid size={12}>
              <Typography variant="subtitle2" color="primary" align="center">{t("form:floor")}: {floor.name}</Typography>
            </Grid>
          )}
          {local && (
            <Grid size={12}>
              <Typography variant="subtitle2" color="primary" align="center">{t("form:local")}: {local.name}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {form &&
        currentPage?.fields
          .sort((a, b) => a.position - b.position)
          .map((field, index) => (
            <Box marginBottom={4} key={index}>
              <Box display={field.valueType === EValueType.Boolean ? "inline" : "block"} marginBottom={1}>
                {field.text && !ignoreTexts.has(field.fieldType) && (
                  <Box display={field.valueType === EValueType.Boolean ? "inline" : "block"}>
                    <Typography display="inline">{field.text.trim()}</Typography>
                    {field.required && <Typography display="inline" color="error"> *</Typography>}
                  </Box>
                )}
              </Box>
              <Box display={field.valueType === EValueType.Boolean ? "inline" : "block"}>{fieldTypes.has(field.fieldType) && (fieldTypes.get(field.fieldType) as Function)(field, form)}</Box>
            </Box>
          ))
      }

      {!isSuccessDisplayed && (
        <Box sx={{ display: "flex", justifyContent: "space-between", padding: 0, marginTop: 6, marginBottom: 2 }}>
          <Progress isLoading={isLoading}>
            <Button sx={{ visibility: currentPageNumber > 1 ? "visible" : "hidden" }} onClick={() => setCurrentPageNumber(currentPageNumber - 1)} variant="contained" startIcon={<ChevronLeftRoundedIcon />}>
              {t('button.back')}
            </Button>

            {form.pages.length > 1 && (
              <Typography sx={{ display: "flex", alignItems: "center" }}>{`${currentPageNumber}/${form.pages.length}`}</Typography>
            )}


            {currentPageNumber < form.pages.length ? (
              <Button disabled={!isRequiredCompleted} onClick={() => setCurrentPageNumber(currentPageNumber + 1)} variant="contained" endIcon={<ChevronRightRoundedIcon />}>
                {t('button.next')}
              </Button>
            ) : (
              <Button disabled={!isRequiredCompleted} onClick={onSubmit} color='success' variant="contained" >
                {t('form:sendForm')}
              </Button>
            )}
          </Progress>
        </Box>
      )}

      {isSuccessDisplayed &&
        <Alert severity="success" sx={{ width: "100%", marginBottom: 4 }}>
          <AlertTitle>{t("form:formThankyou")}</AlertTitle>
          {t("form:formSent")}
        </Alert>
      }
      {isErrorDisplayed &&
        <Alert severity="error" sx={{ width: "100%", marginBottom: 4 }}>
          {t("form:formSentError")}
        </Alert>
      }

    </Paper>
  );
};

export default ServiceRequestForm;
